import { joinPath, Layer, placeFrom, ROUTES, Section } from './routes';

/*
 * Контроллер стопки: раздел, слои над его корнем и история браузера.
 *
 * Каждый открытый экран — запись в истории с меткой { codeNav: глубина } и
 * своим адресом (реестр маршрутов, routes.ts). Единственный путь снятия
 * экрана по кнопке «назад» — history.back() и последующий popstate: так
 * стрелка в шапке и системная «назад» телефона делают ровно одно и то же.
 *
 * Формы и диалоги без адреса — «призраки»: запись в истории есть, слоя нет,
 * адрес повторяет нижний экран; popstate зовёт их onPop (useBackEntry).
 *
 * История передаётся параметром — в тестах это подделка, jsdom в проекте нет.
 */

const MARK = 'codeNav';

export type HistoryLike = {
  readonly state: unknown;
  pushState(state: unknown, title: string, url?: string): void;
  replaceState(state: unknown, title: string, url?: string): void;
  back(): void;
  go(delta: number): void;
};

export type Nav = {
  readonly section: Section;
  /** Слои раздела, без призраков. */
  readonly layers: Layer[];
  /** Растёт при каждом изменении стопки — снимок для useSyncExternalStore. */
  readonly version: number;
  /** Сменить раздел: стопка заменяется, своей записи раздел не заводит. */
  go(section: Section, layers?: Layer[]): void;
  /** Положить экран поверх; тот же ключ на вершине — замена без новой записи. */
  push(item: Layer): void;
  /** Заменить верхний экран своим: запись та же, «назад» вернёт под него. */
  replace(item: Layer): void;
  /** Стопка из одного экрана — так открывается переписка. */
  open(item: Layer): void;
  /** Стрелка «назад» в шапке. */
  back(): void;
  /** К корню раздела: тап по текущей вкладке. */
  reset(): void;
  /** К экрану стопки с этим ключом: всё, что выше, снимается (хлебные крошки). */
  backTo(key: string): void;
  /** Призрак открылся: запись в историю; вернёт номер. */
  entry(onPop: () => void): number;
  /** Призрак закрыли сами: снять запись. */
  remove(id: number): void;
  /** Браузер прислал popstate. */
  onPopState(): void;
  subscribe(fn: () => void): () => void;
};

/** Глубина из метки в записи истории; 0 — запись не наша. */
export function depthOf(state: unknown): number {
  if (!state || typeof state !== 'object') return 0;
  const v = (state as Record<string, unknown>)[MARK];
  return typeof v === 'number' ? v : 0;
}

const mark = (depth: number) => ({ [MARK]: depth });

type Item = { layer: Layer } | { ghost: number; onPop: () => void };
const isLayer = (i: Item): i is { layer: Layer } => 'layer' in i;

export function createNav(history: HistoryLike, pathname: string | null, search = ''): Nav {
  const start = placeFrom(pathname, search);
  let section: Section = start?.section ?? 'home';
  let items: Item[] = (start?.layers ?? []).map((layer) => ({ layer }));
  /** Адрес чужой (комната гостя, стенд): стопку ведём, адрес не пишем. */
  const silent = !start;
  /** Что сделать, когда придёт popstate от нашего же go(): go срабатывает позже pushState. */
  let pending: (() => void) | null = null;
  let seq = 0;
  /** Глубина записи, чью отмотку remove отложил до конца такта; 0 — отложенного нет. */
  let rewind = 0;
  let version = 0;
  const subs = new Set<() => void>();

  const layersOf = (upto = items.length) => items.slice(0, upto).filter(isLayer).map((i) => i.layer);
  const pathOf = (upto = items.length) => joinPath(section, ROUTES[section].path(layersOf(upto)));
  const write = (fn: 'pushState' | 'replaceState', depth: number, url: string) => {
    if (silent) history[fn](mark(depth), '');
    else history[fn](mark(depth), '', url);
  };
  const notify = () => {
    version++;
    subs.forEach((f) => f());
  };
  /** Снять всё с позиции from; призраки закрываются сверху вниз, как системная «назад». */
  const drop = (from: number) => {
    for (const i of items.splice(from).reverse()) if (!isLayer(i)) i.onPop();
  };
  const swapTop = (layer: Layer) => {
    items[items.length - 1] = { layer };
    write('replaceState', items.length, pathOf());
    notify();
  };
  const pushTop = (layer: Layer) => {
    items.push({ layer });
    write('pushState', items.length, pathOf());
    notify();
  };
  /** Собрать записи под стопкой: корень раздела и по одной на слой. */
  const settle = () => {
    write('replaceState', 0, pathOf(0));
    items.forEach((_, i) => write('pushState', i + 1, pathOf(i + 1)));
  };

  // Старт. Свою запись после перезагрузки узнаём по метке: она равна глубине
  // из адреса, и записи под ней уже в истории — только приводим адрес к
  // канону. Иначе (ссылка в новой вкладке, старый адрес) записи собираются.
  // Чужой адрес: стопка пуста, старую метку обнуляем.
  if (!start) {
    if (depthOf(history.state) > 0) history.replaceState(mark(0), '');
  } else if (depthOf(history.state) === items.length) {
    write('replaceState', items.length, pathOf());
  } else {
    settle();
  }

  return {
    get section() {
      return section;
    },
    get layers() {
      return layersOf();
    },
    get version() {
      return version;
    },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },

    push(item) {
      const top = items[items.length - 1];
      if (top && isLayer(top) && top.layer.key === item.key) swapTop(item);
      else pushTop(item);
    },

    // Замена нужна, когда экран уступает место следующему шагу той же работы:
    // форма нового договора — заведённому договору. Без неё пришлось бы
    // снимать форму через back() и класть договор, когда снятие дошло, а это
    // два шага в разных тактах React (зацикливание экрана, 18.09.2026).
    replace(item) {
      const top = items[items.length - 1];
      if (top && isLayer(top)) swapTop(item);
      else pushTop(item);
    },

    open(item) {
      const d = items.length;
      drop(0);
      items = [{ layer: item }];
      if (d === 0) write('pushState', 1, pathOf());
      else if (d === 1) write('replaceState', 1, pathOf());
      else {
        pending = () => write('replaceState', 1, pathOf());
        history.go(-(d - 1));
      }
      notify();
    },

    go(next, start = []) {
      const d = items.length;
      drop(0);
      section = next;
      items = start.map((layer) => ({ layer }));
      if (d === 0) settle();
      else {
        pending = settle;
        history.go(-d);
      }
      notify();
    },

    back() {
      if (!items.length) return;
      if (depthOf(history.state) > 0) {
        history.back();
        return;
      }
      // историю подменили извне — снимаем сами
      drop(items.length - 1);
      write('replaceState', items.length, pathOf());
      notify();
    },

    backTo(key) {
      const at = items.findIndex((it) => isLayer(it) && it.layer.key === key);
      if (at < 0) return;
      const d = items.length - (at + 1);
      if (!d) return;
      // Снимается тем же путём, что и «назад»: history.go и popstate, — чтобы
      // системная «назад» после перехода по крошке вела туда же, куда ждут.
      if (depthOf(history.state) > 0) {
        history.go(-d);
        return;
      }
      drop(at + 1);
      write('replaceState', items.length, pathOf());
      notify();
    },

    reset() {
      const d = items.length;
      if (!d) return;
      drop(0);
      // у записей под стопкой адреса уже правильные — popstate ничего не правит
      if (depthOf(history.state) > 0) history.go(-d);
      else write('replaceState', 0, pathOf());
      notify();
    },

    entry(onPop) {
      const id = ++seq;
      items.push({ ghost: id, onPop });
      // Верхний призрак только что снят и его отмотка ещё не ушла — запись в
      // истории уже стоит на нужной глубине, её и занимаем.
      if (rewind === items.length && depthOf(history.state) === items.length) rewind = 0;
      else write('pushState', items.length, pathOf());
      return id;
    },

    remove(id) {
      const i = items.findIndex((it) => !isLayer(it) && it.ghost === id);
      if (i < 0) return;
      if (i === items.length - 1) {
        items.pop();
        // Верхний призрак закрыли кнопкой — история должна отмотаться, иначе
        // следующая системная «назад» ничего не сделает. Пришедший popstate
        // увидит глубину, равную нашей, и ничего не снимет. Отмотка — не
        // сразу, а в конце такта: экран, который снялся и тут же встал снова
        // (пробное размонтирование StrictMode, быстрое обновление), берёт
        // свою запись обратно в entry(), и отматывать нечего.
        const depth = items.length + 1;
        if (depthOf(history.state) === depth) {
          rewind = depth;
          queueMicrotask(() => {
            if (rewind !== depth || depthOf(history.state) !== depth) return;
            rewind = 0;
            history.back();
          });
        }
        return;
      }
      // Призрак закрылся под слоем (форма закрылась, а поверх уже положили
      // экран): его запись в истории осталась, и глубина обязана совпадать —
      // оставляем надгробие, его снимет popstate или reset.
      items[i] = { ghost: 0, onPop: () => {} };
    },

    onPopState() {
      if (pending) {
        const p = pending;
        pending = null;
        p();
        return;
      }
      const target = depthOf(history.state);
      if (target >= items.length) return;
      drop(target);
      notify();
    },
  };
}
